import React, { useState, useEffect } from 'react';
import { Shield, Check, Lock, Unlock, AlertCircle } from 'lucide-react';
import api from '../api/client';

interface Section {
  id: string;
  label: string;
  requiresPassword: boolean;
}

interface Admin {
  _id: string;
  name?: string;
  email: string;
  isMainAdmin?: boolean;
  adminPermissions?: string[];
}

export default function AccessControl() {
  const [sections, setSections] = useState<Section[]>([]);
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchData = () => {
    Promise.all([
      api.get('/admin-access/sections'),
      api.get('/admin-access/admins')
    ]).then(([s, a]) => {
      setSections(s.data);
      setAdmins(a.data);
    }).catch(err => {
      setError(err.response?.data?.error || 'Ошибка загрузки');
    }).finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, []);

  const togglePermission = (adminId: string, sectionId: string) => {
    setAdmins(prev => prev.map(a => {
      if (a._id !== adminId) return a;
      const perms = a.adminPermissions || [];
      const newPerms = perms.includes(sectionId) ? perms.filter(p => p !== sectionId) : [...perms, sectionId];
      api.put(`/admin-access/admins/${adminId}`, { permissions: newPerms });
      return { ...a, adminPermissions: newPerms };
    }));
  };

  const toggleSectionPassword = async (sectionId: string) => {
    try {
      const res = await api.post(`/admin-access/sections/${sectionId}/toggle`);
      setSections(prev => prev.map(s => s.id === sectionId ? { ...s, requiresPassword: res.data.requiresPassword } : s));
    } catch (err) {
      alert('Ошибка изменения статуса защиты');
    }
  };

  const glassStyle = {
    background: 'rgba(255,255,255,0.65)',
    backdropFilter: 'blur(20px)',
    borderRadius: 20,
    padding: 24,
    border: '1px solid rgba(255,255,255,0.8)',
    boxShadow: '0 8px 32px rgba(31,38,135,0.07)',
    marginBottom: 20,
  };

  if (loading) return <div style={{ padding: 40 }}>Загрузка...</div>;
  if (error) return <div style={{ padding: 40, color: '#ef4444' }}>❌ {error}</div>;

  const otherAdmins = admins.filter(a => !a.isMainAdmin);
  const mainAdmin = admins.find(a => a.isMainAdmin);

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', paddingBottom: 40 }}>
      {/* Заголовок */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <div style={{ width: 48, height: 48, borderRadius: 14, background: 'linear-gradient(135deg, #6a11cb, #2575fc)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
          <Shield size={24} />
        </div>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800 }}>Управление доступом</h1>
          <p style={{ margin: 0, color: '#64748b', fontSize: 14 }}>Назначайте разделы для супер-админов и защищайте их паролями</p>
        </div>
      </div>

      {/* Блок: Защита разделов */}
      <div style={glassStyle}>
        <h3 style={{ margin: '0 0 16px 0', fontSize: 18, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Lock size={20} color="#f59e0b" /> Защита разделов
        </h3>
        <p style={{ margin: '0 0 20px 0', color: '#64748b', fontSize: 14 }}>
          Включите переключатель, чтобы раздел требовал ввода <strong>системного пароля пользователя</strong> при входе. <br/>
          <span style={{ color: '#10b981', fontWeight: 600 }}>Главный админ пароль не вводит.</span>
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
          {sections.map(s => (
            <div key={s.id} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '12px 16px',
              background: s.requiresPassword ? 'rgba(16,185,129,0.08)' : 'rgba(0,0,0,0.03)',
              borderRadius: 12,
              border: s.requiresPassword ? '1px solid #10b981' : '1px solid #e2e8f0',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {s.requiresPassword ? <Lock size={16} color="#10b981" /> : <Unlock size={16} color="#94a3b8" />}
                <span style={{ fontWeight: 600, fontSize: 14 }}>{s.label}</span>
              </div>
              
              {/* Красивый переключатель */}
              <button
                onClick={() => toggleSectionPassword(s.id)}
                style={{
                  width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer', position: 'relative', transition: 'all 0.2s',
                  background: s.requiresPassword ? '#10b981' : '#cbd5e1',
                  padding: 2,
                }}
              >
                <div style={{
                  width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'all 0.2s',
                  transform: s.requiresPassword ? 'translateX(20px)' : 'translateX(0)',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                }} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Блок: Главный админ */}
      {mainAdmin && (
        <div style={glassStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 12, background: 'rgba(245,158,11,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Shield size={20} color="#f59e0b" />
            </div>
            <div>
              <div style={{ fontWeight: 700 }}>{mainAdmin.name || mainAdmin.email}</div>
              <div style={{ fontSize: 13, color: '#64748b' }}>{mainAdmin.email}</div>
            </div>
            <span style={{ marginLeft: 'auto', background: '#f59e0b', color: '#fff', padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700 }}>
              Главный админ — видит всё
            </span>
          </div>
        </div>
      )}

      {/* Блок: Управление правами других админов */}
      <div style={glassStyle}>
        <h3 style={{ margin: '0 0 16px 0', fontSize: 18, fontWeight: 700 }}>Права супер-админов</h3>
        
        {otherAdmins.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>
            <AlertCircle size={32} style={{ marginBottom: 12 }} />
            <p>Нет других супер-админов. Создайте их в разделе «Пользователи».</p>
          </div>
        ) : (
          otherAdmins.map(admin => (
            <div key={admin._id} style={{
              padding: 20,
              background: 'rgba(255,255,255,0.5)',
              borderRadius: 16,
              marginBottom: 16,
              border: '1px solid rgba(0,0,0,0.05)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg, #6a11cb, #2575fc)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14 }}>
                  {(admin.name || admin.email).charAt(0).toUpperCase()}
                </div>
                <div>
                  <div style={{ fontWeight: 700 }}>{admin.name || 'Без имени'}</div>
                  <div style={{ fontSize: 13, color: '#64748b' }}>{admin.email}</div>
                </div>
                <span style={{ marginLeft: 'auto', fontSize: 13, color: '#64748b' }}>
                  {(admin.adminPermissions || []).length} / {sections.length} разделов
                </span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8 }}>
                {sections.map(s => {
                  const isChecked = (admin.adminPermissions || []).includes(s.id);
                  return (
                    <label key={s.id} style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '8px 12px',
                      background: isChecked ? 'rgba(37,117,252,0.1)' : 'rgba(0,0,0,0.02)',
                      borderRadius: 8,
                      cursor: 'pointer',
                      border: isChecked ? '1px solid #2575fc' : '1px solid transparent',
                      transition: 'all 0.2s',
                    }}>
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => togglePermission(admin._id, s.id)}
                        style={{ accentColor: '#2575fc', width: 16, height: 16 }}
                      />
                      <span style={{ fontSize: 13, fontWeight: 500 }}>{s.label}</span>
                      {s.requiresPassword && <Lock size={12} color="#f59e0b" style={{ marginLeft: 'auto' }} />}
                    </label>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
